import styled from "@emotion/styled";
import { countries, Country } from "@mindlercare/schema";
import { Divider, MarketDropdown, Typography } from "@mindlercare/ui-web";
import { useEffect } from "react";
import { useAuth, useInternalAuth } from "../AuthenticationProvider";
import { useStateNavigator } from "../hooks/useStateNavigator";
import { useTranslation } from "../hooks/useTranslation";
import { BackToLogin } from "./BackToLogin";
import { CreateAccount, CreateAccountButton } from "./CreateCognitoAccount";
import { LoginWithQrCode } from "./LoginWithQrCode";
import { MagicLogin } from "./MagicLogin";
import { ResetCognitoPassword } from "./ResetCognitoPassword";
import { SignInOptions } from "./SignInOptions";
import { SignInWithBankIdOnSameDevice } from "./SignInWithBankIdOnSameDevice";
import { SignInWithEmailAndPassword } from "./SignInWithEmailAndPassword";
import { SignInWithPasskey } from "./SignInWithPasskey";
import { UnableToLogin } from "./UnableToLogin";
import { PasskeyOptOutToggle } from "../components/PasskeyOptOutToggle";

export type AuthenticatorProps = {
  onLogin?: () => void;
  onChangeMarket?: (market: Country) => void;
  useMarketSelector?: boolean;
  /**
   * @deprecated Passkey is always enabled.
   * This prop has no effect and will be removed in future versions.
   */
  passkeyEnabled?: boolean;
};

const notSignInFormStates = ["create-account", "reset-password"];
export const Authenticator = ({
  onLogin,
  onChangeMarket,
  useMarketSelector = false,
}: AuthenticatorProps) => {
  const { market, switchMarket, status } = useInternalAuth();
  const { currentState } = useStateNavigator();
  const { logout } = useAuth();

  useEffect(() => {
    if (status === "logged-in") {
      onLogin?.();
      return;
    }

    // write some comment.
    if (currentState?.view !== "login-blocked" && status === "login-blocked") {
      logout();
      return;
    }
  }, [currentState?.view, logout, status, onLogin]);

  if (currentState?.view === "login-blocked") {
    return (
      <div>
        <BackToLogin />
        <UnableToLogin />
      </div>
    );
  }

  return (
    <Container $useMarketSelector={useMarketSelector}>
      {useMarketSelector &&
        !notSignInFormStates.includes(currentState?.view ?? "") && (
          <MarketDropdownContainer>
            <Typography variant="h3">1. Choose market</Typography>
            <MarketDropdown
              label="Market"
              market={currentState?.market ?? market}
              disabled={status === "login-in-progress"}
              onSelectedItemChange={({ selectedItem }) => {
                switchMarket(selectedItem?.codeISO2 ?? countries.SE.codeISO2);
                onChangeMarket?.(selectedItem ?? countries.SE);
              }}
            />
          </MarketDropdownContainer>
        )}
      {currentState?.view === "create-account" ? (
        <CreateAccount />
      ) : currentState?.view === "reset-password" ? (
        <ResetCognitoPassword />
      ) : (
        <SignInForm />
      )}
    </Container>
  );
};

const SignInForm = () => {
  const { currentState } = useStateNavigator();
  const { t } = useTranslation();

  if (notSignInFormStates.includes(currentState?.view ?? "")) {
    return null;
  }

  return (
    <>
      <Typography variant="h3">2. How do you want to sign in?</Typography>
      <SignInOptions />
      {currentState?.view === "magic-login" && <MagicLogin />}
      {currentState?.view === "passkey" && <SignInWithPasskey />}
      {currentState?.view === "bank-id-this-device" && (
        <SignInWithBankIdOnSameDevice />
      )}
      {currentState?.view === "qr-code" && <LoginWithQrCode />}

      {currentState?.view === "email" ? (
        <>
          <SignInWithEmailAndPassword />
          <PasskeyOptOutToggle />
          <Divider text={t("LOGIN_METHOD_DIVIDER")} />
          <CreateAccountButton />
        </>
      ) : (
        <PasskeyOptOutToggle />
      )}
    </>
  );
};

const Container = styled.div<{ $useMarketSelector: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  max-width: 375px;
  min-height: 528px;
  justify-content: ${({ $useMarketSelector }) =>
    $useMarketSelector ? "flex-start" : "center"};
`;

const MarketDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
